import { useCallback } from 'react';
import { getStytchClient } from '../../../../adapters/react/client/headless-client';
export function useStytchCreateSAMLConnection() {
    return useCallback(async (data) => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
                data: null,
            };
        }
        try {
            const result = await stytch.sso.saml.createConnection({
                display_name: data.displayName,
                identity_provider: data.identityProvider,
            });
            return {
                success: true,
                requestId: result.request_id,
                statusCode: result.status_code,
                connection: result.connection,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to create SAML connection',
                data: error,
            };
        }
    }, []);
}
export function useStytchUpdateSAMLConnection() {
    return useCallback(async (data) => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
                data: null,
            };
        }
        try {
            // CRITICAL: Only include fields that are explicitly provided to prevent
            // accidentally clearing existing SSO configuration. Stytch interprets
            // undefined fields as "clear this value" rather than "don't change".
            const updatePayload = {
                connection_id: data.connectionId,
            };
            if (data.displayName !== undefined) {
                updatePayload.display_name = data.displayName;
            }
            if (data.identityProvider !== undefined) {
                updatePayload.identity_provider = data.identityProvider;
            }
            if (data.idpEntityId !== undefined) {
                updatePayload.idp_entity_id = data.idpEntityId;
            }
            if (data.attributeMapping !== undefined) {
                updatePayload.attribute_mapping = data.attributeMapping;
            }
            if (data.idpSsoUrl !== undefined) {
                updatePayload.idp_sso_url = data.idpSsoUrl;
            }
            if (data.x509Certificate !== undefined) {
                updatePayload.x509_certificate = data.x509Certificate;
            }
            if (data.samlConnectionImplicitRoleAssignments !== undefined) {
                updatePayload.saml_connection_implicit_role_assignments =
                    data.samlConnectionImplicitRoleAssignments;
            }
            if (data.samlGroupImplicitRoleAssignments !== undefined) {
                updatePayload.saml_group_implicit_role_assignments = data.samlGroupImplicitRoleAssignments;
            }
            const result = await stytch.sso.saml.updateConnection(updatePayload);
            return {
                success: true,
                requestId: result.request_id,
                statusCode: result.status_code,
                connection: result.connection,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to update SAML connection',
                data: error,
            };
        }
    }, []);
}
/**
 * Update SAML connection using IdP metadata URL.
 * This is the SAFEST way to update a SAML connection - the IdP provides all
 * the configuration (SSO URL, Entity ID, Certificate) via the metadata URL,
 * so there's no risk of accidentally clearing fields.
 */
export function useStytchUpdateSAMLConnectionByURL() {
    return useCallback(async (data) => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
                data: null,
            };
        }
        try {
            const result = await stytch.sso.saml.updateConnectionByURL({
                connection_id: data.connectionId,
                metadata_url: data.metadataUrl,
            });
            return {
                success: true,
                requestId: result.request_id,
                statusCode: result.status_code,
                connection: result.connection,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to update SAML connection from metadata URL',
                data: error,
            };
        }
    }, []);
}
